import * as React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useNotification } from "../../context/notification-provider";
import FilterOptionForm, {
  FilterOptionFormState,
  FilterOptionFormValues,
  FormFieldValue,
  validateFilterOption,
} from "./filter-option-form";
import { updateFilterOption } from "./service";
import { FilterOptionDetail } from "./types";

const FilterOptionEditDialog = ({
  selectedFilterOption,
  open,
  onClose,
}: {
  selectedFilterOption: FilterOptionDetail;
  open: boolean;
  onClose: () => void;
}) => {
  const { showNotification } = useNotification();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [formState, setFormState] = React.useState<FilterOptionFormState>({
    values: { ...selectedFilterOption },
    errors: {},
  });
  const formValues = formState.values;

  const handleFieldChange = React.useCallback(
    (name: keyof FilterOptionFormValues, value: FormFieldValue) => {
      setFormState((previousState) => ({
        ...previousState,
        values: { ...previousState.values, [name]: value },
      }));
    },
    [],
  );

  const handleSubmit = React.useCallback(async () => {
    const { issues } = validateFilterOption(formValues, true);

    if (issues.length > 0) {
      setFormState((previousState) => ({
        ...previousState,
        errors: Object.fromEntries(
          issues.map((issue) => [issue.path[0], issue.message]),
        ),
      }));
      return;
    }

    setFormState((previousState) => ({ ...previousState, errors: {} }));
    setIsSubmitting(true);

    try {
      await updateFilterOption({
        id: selectedFilterOption.id,
        description: formValues.description ?? "",
        isEnabled: formValues.isEnabled ?? true,
        isDefaultSelected: formValues.isDefaultSelected ?? false,
        sortOrder: formValues.sortOrder ?? 0,
        meta: selectedFilterOption.meta ?? null,
      });

      showNotification({
        message: "Filter option updated successfully.",
        severity: "success",
        duration: 3000,
      });

      onClose();
    } catch (updateError) {
      showNotification({
        message: `Failed to update filter option. Reason: ${(updateError as Error).message}`,
        severity: "error",
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [formValues, selectedFilterOption, showNotification, onClose]);

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ borderBottom: 2, mb: 2, borderColor: "divider" }}>
        Edit Filter Option
      </DialogTitle>
      <DialogContent sx={{ pb: 0 }}>
        <FilterOptionForm
          formState={formState}
          isEdit
          onFieldChange={handleFieldChange}
          onSubmit={handleSubmit}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          Update filter option
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilterOptionEditDialog;
